import React, { useEffect, useState, useCallback } from "react";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import Loading from "../components/Loading";
import UserService from "../services/UserService";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { formatValue } from "react-currency-input-field";
import RatesMap from "./RatesMap";
import LazyLoad from "react-lazy-load";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import { abbrRegion } from "../pages/Constants";
import 'react-tooltip/dist/react-tooltip.css';

// Constants
const TOOLTIP_ID = "fifteenDaysAverage";
const SUBSCRIPTION_TYPES = {
    GOLD: 'Gold',
    PLATINUM: 'Platinum'
};

const CURRENCY_CONFIG = {
    locale: 'en-US',
    currency: 'USD'
};

export const LaneData = () => {
    const [state, setState] = useState({
        showResult: false,
        apiMessage: [],
        subscription: false,
        subscriptionType: 'Default',
        enable_assesorials: false
    });

    const { getAccessTokenSilently, user } = useAuth0();

    const createTooltipContent = useCallback((statistics) => {
        if (!statistics) return '';
        
        return [
            `<i></i><em>low</em><u>high</u>`,
            `<i># of stops</i><em>${statistics.lowNumberOfStops}</em><u>${statistics.highNumberOFStops}</u>`,
            `<i>Lead time</i><em>${statistics.lowLeadTime}</em><u>${statistics.highLeadTime}</u>`,
            `<i>Fuel surcharge</i><em>${statistics.lowFuelSurcharge}</em><u>${statistics.highFuelSurcharge}</u>`,
            `<i>Lumper</i><em>${statistics.lowLumper}</em><u>${statistics.highLumper}</u>`,
            `<i>Pallet charge</i><em>${statistics.lowPalletCharge}</em><u>${statistics.highPalletCharge}</u>`,
            `<i>Weight ticket</i><em>${statistics.lowWeightTicket}</em><u>${statistics.highWeightTicket}</u>`,
            `<i>Interchange</i><em>${statistics.lowInterchange}</em><u>${statistics.highInterchange}</u>`,
            `<i>Container fee</i><em>${statistics.lowContainerFee}</em><u>${statistics.highContainerFee}</u>`,
            `<i>Truck order</i><em>${statistics.lowTruckOrder}</em><u>${statistics.highTruckOrder}</u>`,
            `<i>Unloading fees</i><em>${statistics.lowUnloadingFees}</em><u>${statistics.highUnloadingFees}</u>`,
            `<i>Detention</i><em>${statistics.lowDetention}</em><u>${statistics.highDetention}<ui>`
        ].join('<br/>');
    }, []);

    const formatCurrency = useCallback((value, decimalScale, suffix) => {
        return formatValue({
            value: value.toString(),
            intlConfig: CURRENCY_CONFIG,
            decimalScale,
            suffix
        });
    }, []);

    const callApi = useCallback(async () => {
        try {
            const token = await getAccessTokenSilently();
            
            const subscriptionResponse = await UserService.userSubscriptionStatusPair(token, user);
            const subscriptionData = subscriptionResponse.data;
            
            const laneDataResponse = await UserService.getLaneData(token, user);
            
            setState(prevState => ({
                ...prevState,
                showResult: true,
                apiMessage: laneDataResponse.data,
                subscription: subscriptionData.status,
                subscriptionType: subscriptionData.subscriptionType,
                enable_assesorials: subscriptionData?.user?.enable_assesorials || false
            }));
        } catch (error) {
            console.error('Error fetching data:', error);
            setState(prevState => ({
                ...prevState,
                showResult: true,
                apiMessage: []
            }));
        }
    }, [getAccessTokenSilently, user]);

    useEffect(() => {
        callApi();
    }, [callApi]);

    const renderSkeleton = () => (
        <div className="container-fluid" style={{ paddingBottom: 0, marginBottom: 0 }}>
            <SkeletonTheme baseColor="ghostwhite" highlightColor="#ffffff">
                <div className="row" style={{ paddingBottom: 0, marginBottom: 0 }}>
                    <div className="col-sm-4" style={{ paddingBottom: 0, marginBottom: 0 }}>
                        <Skeleton duration={4} count={16} />
                    </div>
                    <div className="col-sm-4" style={{ paddingBottom: 0, marginBottom: 0 }}>
                        <Skeleton duration={4} count={16} />
                    </div>
                    <div className="col-sm-4" style={{ paddingBottom: 0, marginBottom: 0 }}>
                        <Skeleton duration={4} count={16} />
                    </div>
                </div>
            </SkeletonTheme>
        </div>
    );

    const renderLaneCard = (home) => {
        if (!home?.queryCurrentStatistic) return null;

        const { queryCurrentStatistic } = home;
        const isSpecialCard = queryCurrentStatistic.costPerMile.toString() === '0';
        const cardClassName = isSpecialCard ? "card special-card" : "card empty-background";
        const showTooltip = state.subscriptionType === SUBSCRIPTION_TYPES.GOLD || 
                           state.subscriptionType === SUBSCRIPTION_TYPES.PLATINUM;

        const mapProps = {
            isMobile: true,
            pickCityLat: home.pickCityLat,
            pickCityLng: home.pickCityLng,
            dropCityLat: home.dropCityLat,
            dropCityLng: home.dropCityLng,
        };

        const routeDisplay = (
            <div style={{
                backgroundColor: '#6e6f72',
                padding: '4px 12px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            }}>
                <span style={{
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '13px',
                    fontFamily: 'Arial, sans-serif',
                    lineHeight: '1.2'
                }}>
                    {home.startCity} {abbrRegion(home.startState)} → {home.endCity} {abbrRegion(home.endState)}
                    
                </span>
            </div>
        );

        const pricingDisplay = (
            <div style={{
                backgroundColor: '#414141',
                padding: '0 12px'
            }}>
                <span style={{
                    color: '#ffffff',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    fontWeight: '400'
                }}>
                    {home.eqipment}&nbsp;|&nbsp;
                    {formatCurrency(queryCurrentStatistic.costPerMile, 2, '/mile')} &nbsp;|&nbsp;
                    {formatCurrency(queryCurrentStatistic.averageLoadRate, 0, '/load')}
                </span>
            </div>
        );

        return (
            <div key={home.pickCityLat} className="col-sm-4" style={{ paddingBottom: 0, marginBottom: 0 }}>
                <div style={{ paddingBottom: 0, marginBottom: 0 }}>
                    <div className="" style={{ paddingBottom: 0, marginBottom: 0 }}>
                        <div className={cardClassName} style={{ backgroundColor: '#000000', border: 'none', paddingBottom: 0, marginBottom: 0 }}>
                            {state.enable_assesorials ? (
                                <div className="card-body" style={{ backgroundColor: '#000000', padding: '0 0 0 0', margin: 0 }}>
                                    <a 
                                        data-tooltip-id={TOOLTIP_ID} 
                                        data-tooltip-html={createTooltipContent(queryCurrentStatistic)}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        {routeDisplay}
                                        {pricingDisplay}
                                        <div className="rates-map-container" style={{ minHeight: '140px', backgroundColor: '#000000', padding: 0, margin: 0, border: 'none', width: '100%', boxSizing: 'border-box' }}>
                                            <RatesMap latlng={mapProps} />
                                        </div>
                                    </a>
                                    
                                    <div style={{ backgroundColor: '#000000' }}>
                                        {showTooltip && (
                                            <ReactTooltip
                                                className="tooltipss custom-color"
                                                place="left-start"
                                                border
                                                textColor="#5F4B8BFF"
                                                backgroundColor="#E69A8DFF"
                                                borderColor="darkgreen"
                                                arrowColor="red"
                                                id={TOOLTIP_ID}
                                                multiline={true}
                                                positionStrategy="fixed"
                                                offset={10}
                                                style={{ zIndex: 99999 }}
                                            />
                                        )}

                                    </div>
                                </div>
                            ) : (
                                <div className="card-body" style={{ backgroundColor: '#000000', padding: '0 0 0 0', margin: 0 }}>
                                    <div style={{ backgroundColor: '#000000' }}>
                                        {routeDisplay}
                                        {pricingDisplay}
                                        <div className="rates-map-container" style={{ minHeight: '140px', backgroundColor: '#000000', padding: 0, margin: 0, border: 'none', width: '100%', boxSizing: 'border-box' }}>
                                            <RatesMap latlng={mapProps} />
                                        </div>
                                    </div>
                                    
                                    <div style={{ backgroundColor: '#000000' }}>
                                        {state.subscriptionType === SUBSCRIPTION_TYPES.GOLD && (
                                            <ReactTooltip
                                                id={TOOLTIP_ID}
                                                multiline={true}
                                                positionStrategy="fixed"
                                                offset={10}
                                                style={{ zIndex: 99999 }}
                                            />
                                        )}

                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <LazyLoad height={270}>
            <div>
                {!state.showResult && renderSkeleton()}
                <div>
                    <div className="row">
                        {state.showResult && state.apiMessage.map(renderLaneCard)}
                    </div>
                </div>
            </div>
        </LazyLoad>
    );
};

export default withAuthenticationRequired(LaneData, {
    onRedirecting: () => <Loading />,
});